import React from 'react';
import LegalLayout from '../../components/LegalLayout';

const ServiceAgreement = () => (
  <LegalLayout title="MyBusinessAdPro Service Agreement" lastUpdated="February 2026 · Template — personalised &amp; emailed on signup">
    <div className="not-prose mb-6 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 via-rose-50 to-orange-50 p-4 sm:p-5 flex items-start gap-3" data-testid="uk-territory-callout">
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white border border-orange-200 flex items-center justify-center text-lg" aria-hidden="true">
        🇬🇧
      </div>
      <div className="text-sm text-slate-700 leading-relaxed">
        <div className="font-bold text-slate-900">We serve the whole of the UK.</div>
        MyBusinessAdPro is available to small &amp; medium businesses across
        <strong> England, Wales, Scotland, and Northern Ireland</strong>.
        Clients in Scotland and Northern Ireland may bring proceedings in their local courts where required by local mandatory law — see §12.
      </div>
    </div>

    <p className="lead"><em>The live version of this agreement, with Client details auto-filled, is generated by our contract-generator service and emailed as a PDF within seconds of signup.</em></p>

    <p><strong>Agreement Reference:</strong> MBAP-2026-XXXXX<br />
    <strong>Effective Date:</strong> _______________</p>

    <hr />

    <h2>1. Parties</h2>
    <p>This Service Agreement ("<strong>Agreement</strong>") is entered into between:</p>
    <p><strong>Service Provider:</strong><br />
      Krinal Ahir Ltd, trading as MyBusinessAdPro<br />
      The Spinney, 12 Scothern Lane, Sudbrooke LN2 2QT<br />
      Company Registration Number: 11940942<br />
      VAT Number: [VAT NUMBER]<br />
      Hereinafter referred to as <em>"MyBusinessAdPro"</em> or <em>"Provider"</em>.
    </p>
    <p><strong>Client:</strong><br />
      Business Name: _______________<br />
      Business Type: _______________<br />
      Business Address: _______________<br />
      Contact Person: _______________<br />
      Email: _______________<br />
      Hereinafter referred to as <em>"Client"</em> or <em>"Subscriber"</em>.
    </p>

    <h2>2. Services Provided</h2>
    <p>MyBusinessAdPro agrees to provide the following digital marketing services:</p>
    <ul>
      <li>AI-powered advertising content generation (copy + imagery) for social media</li>
      <li>Built-in UK ASA CAP-code compliance checking</li>
      <li>Social-media share helpers for Meta (Facebook / Instagram)</li>
      <li>Google Business Profile management and optimisation helpers</li>
      <li>AI-assisted Google review response generation</li>
      <li>Patient / customer engagement tools (WhatsApp lead capture)</li>
      <li>Analytics and performance reporting dashboard</li>
    </ul>
    <p><em>Note: all advertising content generated is designed to assist compliance with UK ASA CAP-code standards. Final compliance responsibility sits with the Client — see §7.</em></p>

    <h2>3. Subscription Plans and Pricing</h2>
    <table>
      <thead>
        <tr><th>Plan</th><th>Monthly Fee</th><th>Annual Fee (2 months free)</th></tr>
      </thead>
      <tbody>
        <tr><td>Starter</td><td>£9.00</td><td>£90.00</td></tr>
        <tr><td>Pro</td><td>£19.00</td><td>£190.00</td></tr>
        <tr><td>Enterprise</td><td>£49.00</td><td>£490.00</td></tr>
      </tbody>
    </table>
    <p><strong>Selected Plan:</strong> _______________ @ £_____ per month/year</p>

    <h2>4. Free Trial Period</h2>
    <ul>
      <li>All new subscribers are entitled to a <strong>7-day free trial</strong> with full access to their selected plan.</li>
      <li>A valid payment card IS required to start the trial (see <a href="/legal/trial-terms">Trial Terms</a>).</li>
      <li>At the end of the trial period, the subscription <strong>will automatically convert</strong> to the paid plan unless the Client cancels before the Trial End Date.</li>
      <li>The Client may cancel during the trial period without any charge.</li>
    </ul>

    <h2>5. Payment Terms</h2>
    <ul>
      <li><strong>Billing cycle:</strong> Monthly subscriptions are billed on the same date each month. Annual subscriptions are billed once per year.</li>
      <li><strong>Payment method:</strong> Payments are processed securely via Stripe. Accepted methods include credit / debit cards.</li>
      <li><strong>Automatic renewal:</strong> Subscriptions automatically renew at the end of each billing cycle unless cancelled.</li>
      <li><strong>Failed payments:</strong> If a payment fails, the Client will be notified and given 7 days to update payment details before service suspension.</li>
      <li><strong>VAT:</strong> All prices are exclusive of VAT; VAT is added at the applicable rate where registered.</li>
    </ul>

    <h2>6. Cancellation and Refund Policy</h2>
    <ul>
      <li><strong>Cancellation:</strong> The Client may cancel their subscription at any time through the Stripe customer portal or by emailing support.</li>
      <li><strong>Effect of cancellation:</strong> Upon cancellation, access to the platform continues until the end of the current billing period.</li>
      <li><strong>Refunds:</strong> Monthly subscriptions are non-refundable. Annual subscriptions may be eligible for a pro-rata refund within the first 30 days.</li>
      <li><strong>Data retention:</strong> Client data will be retained for 30 days after cancellation, after which it will be permanently deleted.</li>
    </ul>

    <h2>7. Client Responsibilities</h2>
    <p>The Client agrees to:</p>
    <ul>
      <li>Provide accurate business information.</li>
      <li>Review all AI-generated content before publishing to ensure compliance with regulations.</li>
      <li>Maintain the confidentiality of account credentials.</li>
      <li>Comply with Meta, Google, and other platform terms of service when using integrations.</li>
      <li>Not use the service for any unlawful purpose or content that violates UK advertising standards.</li>
    </ul>

    <blockquote>
      <p>⚠️ <strong>Important — Content Responsibility</strong><br />
      <strong>All content created and published is the responsibility of the user of MyBusinessAdPro.</strong> We strongly recommend users always proofread and review content thoroughly to satisfy themselves that content is suitable for posting on relevant platforms.</p>
      <p>Users also need to satisfy themselves that the content they are posting complies with:</p>
      <ul>
        <li>The UK ASA CAP Code and BCAP Code</li>
        <li>The Consumer Protection from Unfair Trading Regulations 2008 and the Digital Markets, Competition and Consumers Act 2024</li>
        <li>The CMA Green Claims Code where environmental claims are made</li>
        <li>Any sector-specific regulation that applies to the Client's industry (e.g. FCA for financial promotions, MHRA for health products, GPhC for pharmacy, etc.)</li>
      </ul>
    </blockquote>

    <blockquote>
      <p>🔞 <strong>Age Restriction Policy</strong><br />
      <strong>All advertisements created through MyBusinessAdPro are strictly targeted to users aged 18 and over.</strong> No advertisements will be displayed to anyone under the age of 18 on ANY platform including Facebook, Instagram, Google, or any other connected service.</p>
    </blockquote>

    <h2>8. Provider Responsibilities</h2>
    <p>MyBusinessAdPro agrees to:</p>
    <ul>
      <li>Provide access to the platform and maintain reasonable uptime (target 99.5%).</li>
      <li>Process and protect Client data in accordance with UK GDPR.</li>
      <li>Provide customer support during business hours (Monday–Friday, 9am–5pm GMT/BST).</li>
      <li>Notify the Client of any planned maintenance or service disruptions.</li>
      <li>Continuously improve and update the platform features.</li>
    </ul>

    <h2>9. Data Protection and Privacy</h2>
    <ul>
      <li>MyBusinessAdPro acts as a <strong>Data Processor</strong> on behalf of the Client (Data Controller) for any personal data the Client uploads about their own customers or leads.</li>
      <li>MyBusinessAdPro is the <strong>Data Controller</strong> for account, billing and engagement data it collects directly about the Client's users and website visitors.</li>
      <li>All personal data is processed in accordance with UK GDPR and the Data Protection Act 2018.</li>
      <li>For full details, see the <a href="/legal/privacy">Privacy Policy</a> and, for enterprise clients, the signed <a href="/legal/dpa">Data Processing Agreement</a>.</li>
    </ul>

    <h2>10. Limitation of Liability</h2>
    <ul>
      <li>MyBusinessAdPro's total liability shall not exceed the fees paid by the Client in the 12 months preceding any claim.</li>
      <li>MyBusinessAdPro is not liable for any advertising content published by the Client without review.</li>
      <li>MyBusinessAdPro is not responsible for changes to third-party platform APIs (Meta, Google) that may affect service functionality.</li>
      <li>The Client is responsible for ensuring their advertising complies with all applicable laws and regulations.</li>
    </ul>

    <h2>11. Term and Termination</h2>
    <ul>
      <li><strong>Term:</strong> this Agreement commences on the Effective Date and continues until terminated.</li>
      <li><strong>Termination for convenience:</strong> either party may terminate with 30 days' written notice.</li>
      <li><strong>Termination for breach:</strong> either party may terminate immediately if the other party materially breaches this Agreement and fails to cure within 14 days of notice.</li>
      <li><strong>Effect of termination:</strong> upon termination, the Client's access will be revoked and data handled per Section 6.</li>
    </ul>

    <h2>12. Service Territory &amp; Governing Law</h2>
    <p><strong>Service Territory.</strong> MyBusinessAdPro is available across the entire United Kingdom — England, Wales, Scotland, and Northern Ireland.</p>
    <p><strong>Governing Law.</strong> This Agreement shall be governed by and construed in accordance with the laws of England and Wales. Disputes shall be subject to the non-exclusive jurisdiction of the courts of England and Wales, without prejudice to the Client's right to bring proceedings in their local UK courts (Scotland or Northern Ireland) where required by mandatory local law.</p>

    <h2>Acceptance and Signatures</h2>
    <p>By signing below, both parties agree to be bound by the terms and conditions of this Service Agreement.</p>
    <p><strong>For and on behalf of MyBusinessAdPro:</strong><br />
      Name: _______________<br />
      Title: _______________<br />
      Date: _______________
    </p>
    <p><strong>For and on behalf of the Client:</strong><br />
      Name: _______________<br />
      Title: _______________<br />
      Date: _______________
    </p>

    <h2>Contact Information</h2>
    <ul>
      <li>General enquiries: <a href="mailto:contact@mybusinessadpro.co.uk">contact@mybusinessadpro.co.uk</a></li>
      <li>Billing enquiries: <a href="mailto:billing@mybusinessadpro.co.uk">billing@mybusinessadpro.co.uk</a></li>
      <li>Support: <a href="mailto:support@mybusinessadpro.co.uk">support@mybusinessadpro.co.uk</a></li>
    </ul>
  </LegalLayout>
);
export default ServiceAgreement;
